import { useEffect, useMemo, useState } from 'react';
import { Roles } from '@touristation/shared';
import { useAuth } from '../auth/AuthProvider';
import {
  useCompliance, useCourseStats, useCourses, useDeleteCourse, useEnrol,
  useLearningOverview, useMyCourses, useRemoveAssignment,
} from '../api/hooks';
import type { LearningCourse } from '../api/types';
import { useT } from '../i18n/LangProvider';
import { Icon, type IconName } from '../ui/Icon';
import { useConfirm } from '../ui/confirm';
import { AssignModal } from '../components/learning/AssignModal';
import { CourseBuilder } from '../components/learning/CourseBuilder';
import { CoursePlayer } from '../components/learning/CoursePlayer';
import { Empty, Ring, StatCard, courseMinutes, fmtDue, isOverdue } from '../components/learning/atoms';

type View =
  | { name: 'list' }
  | { name: 'course'; id: string }
  | { name: 'builder'; courseId: string | null }
  | { name: 'analytics'; id: string };

type Tab = 'my' | 'catalog' | 'manage' | 'dashboard';

// ── course card ────────────────────────────────────────────────────────────

function CourseCard({ course, canManage, onOpen, onEdit, onAssign, onStats, onEnrol }: {
  course: LearningCourse; canManage: boolean;
  onOpen: () => void; onEdit: () => void; onAssign: () => void; onStats: () => void; onEnrol: () => void;
}) {
  const tr = useT();
  const a = course.myAssignment;
  const late = isOverdue(a);
  const lessons = course.lessonCount ?? course.lessons.length;

  return (
    <div className="card lrn-course" style={{ padding: 16 }}>
      <div className="lrn-course__top">
        <div className={`lrn-course__ico is-${course.color ?? 'orange'}`}>
          <Icon name={(course.icon || 'cap') as IconName} size={19} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="lrn-course__cat">{tr(course.category || 'Generale')}</div>
          <div className="lrn-course__title">{course.title}</div>
        </div>
        {a && <Ring value={a.progress} />}
      </div>

      {course.description && <div className="lrn-course__desc">{course.description}</div>}

      <div className="lrn-course__meta">
        <span><Icon name="list" size={12} /> {tr('{n} lezioni', { n: lessons })}</span>
        <span><Icon name="clock" size={12} /> {course.durationMin ?? courseMinutes(course.lessons)} min</span>
        {course.mandatory && <span className="is-danger"><Icon name="warn" size={12} /> {tr('Obbligatorio')}</span>}
        {!course.published && <span><Icon name="eyeOff" size={12} /> {tr('Bozza')}</span>}
        {a?.dueAt && (
          <span className={late ? 'is-danger' : ''}>
            <Icon name="calendar" size={12} /> {late ? tr('Scaduto il ') : tr('Entro il ')}{fmtDue(a.dueAt)}
          </span>
        )}
      </div>

      <div className="lrn-course__actions">
        {a ? (
          <button className="btn btn--sm btn--brand" onClick={onOpen}>
            <Icon name={a.status === 'completed' ? 'check' : 'play'} size={12} />
            {a.status === 'completed' ? tr('Rivedi') : a.progress > 0 ? tr('Riprendi') : tr('Inizia')}
          </button>
        ) : course.openToAll ? (
          <button className="btn btn--sm btn--brand" onClick={onEnrol}><Icon name="plus" size={12} /> {tr('Iscrivimi')}</button>
        ) : (
          <button className="btn btn--sm" onClick={onOpen}><Icon name="eye" size={12} /> {tr('Anteprima')}</button>
        )}
        {canManage && (
          <>
            <button className="btn btn--sm" onClick={onEdit} title={tr('Modifica')}><Icon name="edit" size={12} /></button>
            <button className="btn btn--sm" onClick={onAssign} title={tr('Assegna')}><Icon name="send" size={12} /></button>
            <button className="btn btn--sm" onClick={onStats} title={tr('Statistiche')}><Icon name="chart" size={12} /></button>
          </>
        )}
      </div>
    </div>
  );
}

// ── analytics for one course ───────────────────────────────────────────────

function CourseAnalytics({ courseId, onBack, onAssign, onEdit, onDelete, canDelete }: {
  courseId: string; onBack: () => void; onAssign: () => void; onEdit: () => void;
  onDelete: () => void; canDelete: boolean;
}) {
  const tr = useT();
  const confirm = useConfirm();
  const statsQ = useCourseStats(courseId);
  const removeAssignment = useRemoveAssignment();
  const s = statsQ.data;

  if (statsQ.isLoading) return <div className="lrn-empty">{tr('Carico le statistiche…')}</div>;
  if (!s) return <Empty title={tr('Statistiche non disponibili')} action={<button className="btn" onClick={onBack}>{tr('Indietro')}</button>} />;

  return (
    <div className="lrn">
      <div className="lrn-subbar">
        <button className="lrn-back" onClick={onBack}><Icon name="chevL" size={13} /> {tr('Formazione')}</button>
        <div className="lrn-subbar__title"><Icon name="chart" size={16} /> {s.title}</div>
        <div className="cluster">
          <button className="btn btn--sm" onClick={onEdit}><Icon name="edit" size={12} /> {tr('Modifica')}</button>
          <button className="btn btn--sm btn--brand" onClick={onAssign}><Icon name="send" size={12} /> {tr('Assegna')}</button>
          {canDelete && (
            <button className="btn btn--sm btn--danger" onClick={onDelete}><Icon name="trash" size={12} /> {tr('Elimina')}</button>
          )}
        </div>
      </div>

      <div className="lrn-stats-row">
        <StatCard label={tr('Assegnazioni')} value={s.counts.total} icon="users" tone="neutral" />
        <StatCard label={tr('Completati')} value={s.counts.completed} icon="check" tone="ok"
          sub={s.counts.total ? tr('{n}% del totale', { n: Math.round((s.counts.completed / s.counts.total) * 100) }) : undefined} />
        <StatCard label={tr('In corso')} value={s.counts.inProgress} icon="play" tone="info" />
        <StatCard label={tr('Non iniziati')} value={s.counts.notStarted} icon="clock" tone="warn" />
        <StatCard label={tr('In ritardo')} value={s.counts.overdue} icon="warn" tone="danger" />
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="lrn-table-wrap">
          <table className="lrn-table">
            <thead>
              <tr>
                <th>{tr('Persona')}</th><th>{tr('Stato')}</th><th>{tr('Avanzamento')}</th>
                <th>Quiz</th><th>{tr('Scadenza')}</th><th>{tr('Completato il')}</th><th />
              </tr>
            </thead>
            <tbody>
              {s.rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className="lrn-statline__name">
                      <span className="lrn-avatar is-neutral">{r.employee?.initials ?? r.employee?.name.slice(0, 2).toUpperCase()}</span>
                      {r.employee?.name ?? '—'}
                    </div>
                  </td>
                  <td>
                    <span className={`lrn-ltype ${r.status === 'completed' ? 'is-quiz' : r.status === 'in-progress' ? 'is-document' : 'is-link'}`}>
                      {r.status === 'completed' ? tr('Completato') : r.status === 'in-progress' ? tr('In corso') : tr('Non iniziato')}
                    </span>
                  </td>
                  <td style={{ minWidth: 130 }}>
                    <div className="progress"><div className="progress__bar" style={{ width: `${r.progress}%` }} /></div>
                    <span className="muted" style={{ fontSize: 11.5 }}>{r.progress}%</span>
                  </td>
                  <td>{r.quizScore != null ? `${r.quizScore}%` : '—'}</td>
                  <td className={isOverdue(r) ? 'is-danger-txt' : ''}>{fmtDue(r.dueAt) ?? '—'}</td>
                  <td>{fmtDue(r.completedAt) ?? '—'}</td>
                  <td>
                    <button className="icon-btn icon-btn--danger" title={tr('Rimuovi assegnazione')}
                      onClick={async () => { if (await confirm({ message: tr('Rimuovere l’assegnazione a {name}?', { name: r.employee?.name ?? '' }), confirmLabel: tr('Rimuovi'), danger: true })) removeAssignment.mutate(r.id); }}>
                      <Icon name="trash" size={12} />
                    </button>
                  </td>
                </tr>
              ))}
              {s.rows.length === 0 && (
                <tr><td colSpan={7}><div className="muted" style={{ padding: 16 }}>{tr('Nessuno ha ancora questo corso assegnato.')}</div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── compliance: overdue mandatory courses ───────────────────────────────────

function CompliancePanel({ onOpenCourse }: { onOpenCourse: (id: string) => void }) {
  const tr = useT();
  const q = useCompliance();
  const rows = q.data?.overdue ?? [];

  return (
    <div className="card lrn-panel" style={{ padding: 16 }}>
      <div className="lrn-panel__head">
        <h4><Icon name="warn" size={14} /> {tr('Obbligatori scaduti')}</h4>
        {rows.length > 0 && (
          <span className="lrn-chips__n" style={{ background: 'var(--danger-soft)', color: 'var(--danger)' }}>
            {tr('{n} persone', { n: q.data?.peopleAffected ?? 0 })}
          </span>
        )}
      </div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        {tr('Chi non ha completato un corso obbligatorio oltre la scadenza.')}
      </div>
      {q.isLoading ? (
        <div className="muted" style={{ fontSize: 13 }}>{tr('Carico…')}</div>
      ) : rows.length === 0 ? (
        <div className="lrn-allgood" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--ok)', fontSize: 13.5 }}>
          <Icon name="check" size={15} /> {tr('Nessuno in ritardo. Tutto in regola.')}
        </div>
      ) : (
        <div className="col" style={{ gap: 6 }}>
          {rows.map((r) => (
            <button key={r.assignmentId} className="lrn-statline" style={{ textAlign: 'left' }}
              onClick={() => onOpenCourse(r.course.id)}>
              <span className="lrn-statline__name">
                <span className="lrn-avatar is-danger">{r.employee?.initials ?? r.employee?.name.slice(0, 2).toUpperCase()}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.employee?.name}</span>
                  <span className="muted" style={{ fontSize: 11.5 }}>{r.course.title}</span>
                </span>
              </span>
              <span className="muted" style={{ fontSize: 11.5 }}>{r.progress}%</span>
              <span className="lrn-statline__val is-danger-txt">{tr('{n}g in ritardo', { n: r.daysLate })}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── screen ─────────────────────────────────────────────────────────────────

export function LearningScreen() {
  const tr = useT();
  const confirm = useConfirm();
  const auth = useAuth();
  const canManage = auth.hasRole(Roles.ADMIN) || auth.hasRole(Roles.TEAM_LEAD);

  const coursesQ = useCourses();
  const mineQ = useMyCourses();
  const overviewQ = useLearningOverview(canManage);
  const enrol = useEnrol();
  const removeCourse = useDeleteCourse();

  const [tab, setTab] = useState<Tab>('my');
  const [view, setView] = useState<View>({ name: 'list' });
  const [assignFor, setAssignFor] = useState<string | null | undefined>(undefined);
  const [toast, setToast] = useState<{ msg: string; key: number } | null>(null);
  const [search, setSearch] = useState('');

  const showToast = (msg: string) => setToast({ msg, key: Date.now() });
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const courses = coursesQ.data ?? [];
  const mine = mineQ.data ?? [];

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return courses;
    return courses.filter((c) =>
      [c.title, c.category, c.description].some((v) => (v ?? '').toLowerCase().includes(t)));
  }, [courses, search]);

  const myStats = useMemo(() => {
    const total = mine.length;
    const completed = mine.filter((a) => a.status === 'completed').length;
    const late = mine.filter((a) => isOverdue(a)).length;
    return { total, completed, late, pct: total ? Math.round((completed / total) * 100) : 0 };
  }, [mine]);

  const toastEl = toast && <div key={toast.key} className="lrn-toast" role="status" aria-live="polite"><Icon name="check" size={13} /> {toast.msg}</div>;
  const modalEl = assignFor !== undefined && (
    <AssignModal presetCourseId={assignFor} onClose={() => setAssignFor(undefined)} onToast={showToast} />
  );

  // ── full-page views ──
  if (view.name === 'course') {
    return <div className="content">
      <CoursePlayer courseId={view.id} onBack={() => setView({ name: 'list' })} onToast={showToast} />
      {toastEl}
    </div>;
  }
  if (view.name === 'builder' && canManage) {
    return <div className="content">
      <CourseBuilder courseId={view.courseId}
        onCancel={() => setView({ name: 'list' })}
        onSaved={(id) => { setTab('catalog'); setView({ name: 'analytics', id }); }}
        onToast={showToast} />
      {toastEl}
    </div>;
  }
  if (view.name === 'analytics' && canManage) {
    return <div className="content">
      <CourseAnalytics courseId={view.id}
        onBack={() => setView({ name: 'list' })}
        onAssign={() => setAssignFor(view.id)}
        onEdit={() => setView({ name: 'builder', courseId: view.id })}
        canDelete={auth.hasRole(Roles.ADMIN)}
        onDelete={async () => {
          const c = courses.find((x) => x.id === view.id);
          if (await confirm({ message: tr('Eliminare “{title}”? Anche i progressi di tutti andranno persi.', { title: c?.title ?? tr('questo corso') }), confirmLabel: tr('Elimina'), danger: true })) {
            removeCourse.mutate(view.id, { onSuccess: () => { showToast(tr('Corso eliminato')); setView({ name: 'list' }); } });
          }
        }} />
      {modalEl}{toastEl}
    </div>;
  }

  const openCourse = (id: string) => setView({ name: 'course', id });
  const cardProps = (c: LearningCourse) => ({
    course: c,
    canManage,
    onOpen: () => openCourse(c.id),
    onEdit: () => setView({ name: 'builder', courseId: c.id }),
    onAssign: () => setAssignFor(c.id),
    onStats: () => setView({ name: 'analytics', id: c.id }),
    onEnrol: async () => {
      try { await enrol.mutateAsync(c.id); openCourse(c.id); }
      catch (e) { showToast(e instanceof Error ? e.message : tr('Iscrizione non riuscita')); }
    },
  });

  const tabs: { id: Tab; label: string; shortLabel: string; icon: IconName }[] = [
    { id: 'my', label: 'I miei percorsi', shortLabel: 'Percorsi', icon: 'cap' },
    { id: 'catalog', label: 'Catalogo', shortLabel: 'Catalogo', icon: 'list' },
    ...(canManage ? [
      { id: 'manage' as Tab, label: 'Gestione & invio', shortLabel: 'Gestione', icon: 'send' as IconName },
      { id: 'dashboard' as Tab, label: 'Dashboard', shortLabel: 'Report', icon: 'chart' as IconName },
    ] : []),
  ];

  return (
    <div className="content lrn">
      <div className="page-head">
        <div>
          <h1 className="h1">{tr('Formazione')}</h1>
          <div className="page-head__sub">{tr('Percorsi aziendali: articoli, video, documenti, link e quiz.')}</div>
        </div>
        {canManage && (
          <div className="cluster">
            <button className="btn" onClick={() => setView({ name: 'builder', courseId: null })}>
              <Icon name="plus" size={14} /> {tr('Nuovo corso')}
            </button>
            <button className="btn btn--brand" onClick={() => setAssignFor(null)}>
              <Icon name="send" size={14} /> {tr('Invia formazione')}
            </button>
          </div>
        )}
      </div>

      <div className="lrn-tabs" role="tablist" aria-label={tr('Sezioni della formazione')}>
        {tabs.map((t) => (
          <button
            key={t.id}
            id={`learning-tab-${t.id}`}
            className={`lrn-tab ${tab === t.id ? 'is-active' : ''}`}
            type="button"
            role="tab"
            aria-label={tr(t.label)}
            aria-selected={tab === t.id}
            aria-current={tab === t.id ? 'page' : undefined}
            aria-controls={`learning-panel-${t.id}`}
            tabIndex={tab === t.id ? 0 : -1}
            onClick={() => setTab(t.id)}
            onKeyDown={(event) => {
              const key = event.key;
              if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return;
              event.preventDefault();
              const currentIndex = tabs.findIndex((item) => item.id === t.id);
              const nextIndex = key === 'Home'
                ? 0
                : key === 'End'
                  ? tabs.length - 1
                  : (currentIndex + (key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
              const next = tabs[nextIndex];
              setTab(next.id);
              requestAnimationFrame(() => document.getElementById(`learning-tab-${next.id}`)?.focus());
            }}
          >
            <Icon name={t.icon} size={14} />
            <span className="lrn-tab__label--long">{tr(t.label)}</span>
            <span className="lrn-tab__label--short" aria-hidden="true">{tr(t.shortLabel)}</span>
          </button>
        ))}
      </div>

      {/* ── I miei percorsi ── */}
      {tab === 'my' && (
        <section id="learning-panel-my" role="tabpanel" aria-labelledby="learning-tab-my" tabIndex={0}>
          <div className="lrn-stats-row lrn-mystats">
            <StatCard label={tr('Assegnati')} value={myStats.total} icon="cap" tone="orange" />
            <StatCard label={tr('Completati')} value={myStats.completed} icon="check" tone="ok" sub={tr('{n}% del totale', { n: myStats.pct })} />
            <StatCard label={tr('In ritardo')} value={myStats.late} icon="warn" tone={myStats.late ? 'danger' : 'neutral'} />
          </div>

          {mine.length === 0 ? (
            <Empty title={tr('Nessun corso assegnato')}
              hint={tr('Guarda il catalogo: puoi iscriverti da solo ai corsi aperti.')}
              action={<button className="btn btn--brand" onClick={() => setTab('catalog')}>{tr('Vai al catalogo')}</button>} />
          ) : (
            <div className="lrn-grid">
              {mine.map((a) => (
                <CourseCard key={a.id} {...cardProps({ ...a.course, myAssignment: a })} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── Catalogo ── */}
      {tab === 'catalog' && (
        <section id="learning-panel-catalog" role="tabpanel" aria-labelledby="learning-tab-catalog" tabIndex={0}>
          <div className="row" style={{ gap: 10, marginBottom: 16, alignItems: 'center' }}>
            <div className="lrn-search">
              <Icon name="search" size={13} />
              <input placeholder={tr('Cerca un corso…')} value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <span className="muted" style={{ fontSize: 12.5 }}>{tr('{n} corsi', { n: filtered.length })}</span>
          </div>

          {filtered.length === 0 ? (
            <Empty title={tr('Nessun corso')}
              hint={canManage ? tr('Crea il primo corso con “Nuovo corso”.') : tr('Non ci sono ancora corsi pubblicati.')}
              action={canManage ? <button className="btn btn--brand" onClick={() => setView({ name: 'builder', courseId: null })}>{tr('Nuovo corso')}</button> : undefined} />
          ) : (
            <div className="lrn-grid">
              {filtered.map((c) => <CourseCard key={c.id} {...cardProps(c)} />)}
            </div>
          )}
        </section>
      )}

      {/* ── Gestione & invio ── */}
      {tab === 'manage' && canManage && (
        <section id="learning-panel-manage" role="tabpanel" aria-labelledby="learning-tab-manage" tabIndex={0}>
          <div className="card" style={{ padding: 0 }}>
            <div className="lrn-table-wrap">
              <table className="lrn-table">
              <thead>
                <tr><th>{tr('Corso')}</th><th>{tr('Categoria')}</th><th>{tr('Lezioni')}</th><th>{tr('Stato')}</th><th>{tr('Accesso')}</th><th /></tr>
              </thead>
              <tbody>
                {courses.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div className="lrn-statline__name">
                        <span className={`lrn-course__ico is-${c.color ?? 'orange'}`} style={{ width: 28, height: 28 }}>
                          <Icon name={(c.icon || 'cap') as IconName} size={14} />
                        </span>
                        <span>
                          {c.title}
                          {c.mandatory && <span className="lrn-chips__n">{tr('obbligatorio')}</span>}
                        </span>
                      </div>
                    </td>
                    <td>{c.category || '—'}</td>
                    <td>{c.lessonCount ?? c.lessons.length}</td>
                    <td>
                      <span className={`lrn-ltype ${c.published ? 'is-quiz' : 'is-link'}`}>
                        {c.published ? tr('Pubblicato') : tr('Bozza')}
                      </span>
                    </td>
                    <td>{c.openToAll ? tr('Aperto a tutti') : tr('Solo su assegnazione')}</td>
                    <td>
                      <div className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                        <button className="icon-btn" title={tr('Modifica')} onClick={() => setView({ name: 'builder', courseId: c.id })}><Icon name="edit" size={12} /></button>
                        <button className="icon-btn" title={tr('Assegna')} onClick={() => setAssignFor(c.id)}><Icon name="send" size={12} /></button>
                        <button className="icon-btn" title={tr('Statistiche')} onClick={() => setView({ name: 'analytics', id: c.id })}><Icon name="chart" size={12} /></button>
                        {auth.hasRole(Roles.ADMIN) && (
                          <button className="icon-btn icon-btn--danger" title={tr('Elimina')}
                            onClick={async () => {
                              if (await confirm({ message: tr('Eliminare “{title}”? Anche i progressi di tutti andranno persi.', { title: c.title }), confirmLabel: tr('Elimina'), danger: true })) {
                                removeCourse.mutate(c.id, { onSuccess: () => showToast(tr('Corso eliminato')) });
                              }
                            }}>
                            <Icon name="trash" size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {courses.length === 0 && (
                  <tr><td colSpan={6}><div className="muted" style={{ padding: 16 }}>{tr('Nessun corso creato.')}</div></td></tr>
                )}
              </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* ── Dashboard ── */}
      {tab === 'dashboard' && canManage && (
        <section id="learning-panel-dashboard" role="tabpanel" aria-labelledby="learning-tab-dashboard" tabIndex={0}>
          <div className="lrn-stats-row">
            <StatCard label={tr('Corsi')} value={overviewQ.data?.courses ?? 0} icon="cap" tone="orange"
              sub={tr('{n} pubblicati', { n: overviewQ.data?.published ?? 0 })} />
            <StatCard label={tr('Assegnazioni')} value={overviewQ.data?.assignments ?? 0} icon="users" tone="info" />
            <StatCard label={tr('Completamento')} value={`${overviewQ.data?.completionRate ?? 0}%`} icon="check" tone="ok"
              sub={tr('{n} completati', { n: overviewQ.data?.completed ?? 0 })} />
            <StatCard label={tr('In ritardo')} value={overviewQ.data?.overdue ?? 0} icon="warn"
              tone={overviewQ.data?.overdue ? 'danger' : 'neutral'} />
            <StatCard label={tr('Obbligatori')} value={overviewQ.data?.mandatory ?? 0} icon="shield" tone="warn" />
          </div>

          <div className="lrn-dash__cols">
            <div className="card lrn-panel" style={{ padding: 16 }}>
              <div className="lrn-panel__head"><h4>{tr('Corsi per completamento')}</h4></div>
              {courses.length === 0 && <div className="muted" style={{ fontSize: 13 }}>{tr('Nessun corso da mostrare.')}</div>}
              {courses.map((c) => (
                <button key={c.id} className="lrn-statline" style={{ textAlign: 'left' }}
                  onClick={() => setView({ name: 'analytics', id: c.id })}>
                  <span className="lrn-statline__name">
                    <span className={`lrn-dot is-${c.color ?? 'orange'}`} style={{ width: 10, height: 10, borderRadius: '50%' }} />
                    {c.title}
                  </span>
                  <span className="muted" style={{ fontSize: 12 }}>{tr('{n} lezioni', { n: c.lessonCount ?? c.lessons.length })}</span>
                  <span className="lrn-statline__val">{c.published ? tr('Pubblicato') : tr('Bozza')}</span>
                </button>
              ))}
            </div>

            <CompliancePanel onOpenCourse={(id) => setView({ name: 'analytics', id })} />
          </div>
        </section>
      )}

      {modalEl}
      {toastEl}
    </div>
  );
}
